/**
 * @class
 */
import Base from './attribute';

import {ids as attributeTypeIds, numberTypes} from '../properties/attributes';

// Polyfill
// Safari did not have this prior to version 10 (currently at v11 but just to be safe)
if (!window.Intl) {
	window.Intl = {
		NumberFormat(locale, options) {
			const digits = '0123456789';
			this.format = (n) => {
				let s = n.toString();
				if (
					!options ||
					options.useGrouping === undefined ||
					options.useGrouping
				) {
					var dp = s.indexOf('.');
					if (dp < 0) {
						dp = s.length;
					}
					while ((dp -= 3) > 0 && digits.indexOf(s[dp - 1]) >= 0) {
						s = s.substr(0, dp) + ',' + s.substr(dp);
					}
				}
				return s;
			};
		},
	};
}
/**
 * @function
 * @param {number} value Value
 * @return {number} Decimals
 */
function getDecimalCount(value) {
	value = value + '';

	const decimalSeparator = whatDecimalSeparator();
	const decimals = value.split('.');

	if (!decimals[1]) {
		return 0;
	}

	return decimals[1].length;
}
/**
 * https://stackoverflow.com/questions/11665884/how-can-i-parse-a-string-with-a-comma-thousand-separator-to-a-number
 * @function
 * @param {mixed} value Value
 * @param {string} locale Locale
 * @return {number}
 */
function parseNumber(value, locale) {
	value = value.toString();
	locale = locale || navigator.language;
	const example = Intl.NumberFormat(locale).format('1.1');
	const cleanPattern = new RegExp('[^-+0-9' + example.charAt(1) + ']', 'g');
	const cleaned = value.replace(cleanPattern, '');
	const normalized = cleaned.replace(example.charAt(1), '.');

	return parseFloat(normalized);
}
/**
 * @function
 * @return {string} Decimal separator for locale
 */
function whatDecimalSeparator() {
	let n = 1.1;
	n = n.toLocaleString().substring(1, 2);
	return n;
}

export default class Attribute_Number extends Base {
	/** @type {object} */
	get defaults() {
		return {
			...super.defaults,
			type: attributeTypeIds.number,
		};
	}

	/**
	 * @override
	 * @param {mixed} value Value
	 * @param {object} opts Options
	 * @return {mixed} Formatted value
	 */
	format(value, opts = {}) {
		value = this.parseInValue(value);

		// Do not parse into locale string
		const noLocale = opts.noLocale === true;
		const numberType = numberTypes[this.get('spec2')] || {};
		const maxDecimal = numberType.maxDecimal || 0;

		/**
		 * @function
		 * @param {number} value Value
		 * @return Parsed number
		 */
		const parse = (value) => {
			value = parseNumber(value);

			// `Unlimited` number type
			if (maxDecimal === Infinity) {
				return noLocale
					? value.toString()
					: value.toLocaleString(undefined, {
							useGrouping: false,
							maximumFractionDigits: 20, // Maximum allowed by`toLocaleString`
					  });
			} else {
				return noLocale
					? value.toFixed(maxDecimal)
					: value.toLocaleString(undefined, {
							useGrouping: false,
							minimumFractionDigits: maxDecimal,
							maximumFractionDigits: maxDecimal,
					  });
			}
		};

		if (this.get('system')) {
			// Normalize
			switch (this.get('name')) {
				case 'fileSize':
				case 'fileSize2':
					value = _.bytes(value, true);
					break;
			}
		} else if (value) {
			value = _.isArray(value) ? _.map(value, parse) : parse(value);
		}

		return super.format(value);
	}

	/**
	 * Fix for displaying floats with zeros
	 * @override
	 * @param {object|string} key The key or an object of key values
	 * @param {mixed} value Either the value, or if the key is an object, the options
	 * @param {object} opts Either the options, or if the key is an object, undefined
	 */
	set(key, value, opts) {
		// Make life easier
		let attrs;
		if (_.isObject(key)) {
			attrs = key;
			opts = value;
		} else {
			attrs = {
				[key]: value,
			};
		}

		if (attrs.value) {
			// Check if we are enforcing decimals via spec2 (a value of 4 means it is free - whole/decimal whatever)
			const decimalPlaces = attrs.spec2 || this.get('spec2');
			if (decimalPlaces && decimalPlaces <= 3) {
				attrs.value = Number(attrs.value).toFixed(decimalPlaces);
			}
		}

		// Super
		return super.set(attrs, opts);
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @return {boolean} Invalid
	 */
	invalid(value) {
		let valid = this.validator(value);

		if (valid === true) {
			return false;
		}

		if (valid instanceof Error) {
			return valid.message;
		}

		return i18n.t('i18n.invalid');
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @return {boolean} Valid
	 */
	validator(value) {
		// Empty values are fine
		// TODO: check for mandatory
		if (_.isNull(value) || _.isUndefined(value) || value === '') {
			return true;
		}

		value = value.toString();

		const numberType = numberTypes[this.get('spec2')] || false;
		const maxDecimal = numberType && numberType.maxDecimal;
		const maxLength =
			numberType.key === 'whole' ? this.get('spec1') : undefined;

		const decimalIndex = value.indexOf('.');
		if (decimalIndex > -1 && decimalIndex + 1 === value.length) {
			return new Error('Invalid number');
		}

		if (maxLength && value.length > maxLength) {
			return new Error(`Exceeds max length of ${maxLength}`);
		}

		if (isNaN(parseNumber(value))) {
			return new Error('Invalid number format');
		}

		// Negative numbers are not allowed by the DAM
		if (numberType.key === 'unlimited' && value < 0) {
			return new Error('Negative numbers are not allowed');
		}

		const decimalCount = getDecimalCount(value);
		if (decimalCount > maxDecimal) {
			return new Error(`Exceeds max decimals of ${maxDecimal}`);
		}

		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'Attribute_Number';
	}
}
